import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, Users, Eye, MousePointer, Clock, Globe, ArrowUp, ArrowDown } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMemo } from "react";
import { startOfWeek, format, subDays } from "date-fns";
import { useRealtimeSubscription } from "@/hooks/use-realtime";
import { LiveVisitors } from "./LiveVisitors";
import { VisitorMap } from "./VisitorMap";

export function TrafficAnalytics() {
  const [period, setPeriod] = useState("7");

  const { data: analytics } = useQuery({
    queryKey: ["website-analytics", period],
    queryFn: async () => {
      const days = parseInt(period);
      const startDate = subDays(new Date(), days);
      
      const { data, error } = await supabase
        .from("website_analytics")
        .select("*")
        .gte("visited_at", startDate.toISOString());

      if (error) throw error;
      return data || [];
    },
  });

  // Real-time subscription
  useRealtimeSubscription("website_analytics", ["website-analytics", period]);

  const trafficData = useMemo(() => {
    const days = parseInt(period);
    if (!analytics || analytics.length === 0) {
      return Array.from({ length: days }, (_, i) => ({
        date: format(subDays(new Date(), days - 1 - i), "MMM dd"),
        visitors: 0,
        pageViews: 0,
        uniqueVisitors: 0,
      }));
    }

    const dataPoints = Array.from({ length: days }, (_, i) => {
      const date = subDays(new Date(), days - 1 - i);
      const dayData = analytics.filter(
        (a) => format(new Date(a.visited_at), "yyyy-MM-dd") === format(date, "yyyy-MM-dd")
      );

      return {
        date: format(date, "MMM dd"),
        visitors: dayData.length,
        pageViews: dayData.length,
        uniqueVisitors: new Set(dayData.map((d) => d.visitor_id)).size,
      };
    });

    return dataPoints;
  }, [analytics, period]);

  const topPages = useMemo(() => {
    if (!analytics || analytics.length === 0) return [];

    const pageCount = analytics.reduce((acc, curr) => {
      acc[curr.page_path] = (acc[curr.page_path] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const avgTime = analytics.reduce((acc, curr) => {
      if (!acc[curr.page_path]) acc[curr.page_path] = [];
      if (curr.time_on_page) acc[curr.page_path].push(curr.time_on_page);
      return acc;
    }, {} as Record<string, number[]>);

    return Object.entries(pageCount)
      .map(([page, views]) => ({
        page,
        views,
        avgTime: avgTime[page]?.length
          ? `${Math.floor(avgTime[page].reduce((a, b) => a + b, 0) / avgTime[page].length / 60)}:${String(
              Math.floor((avgTime[page].reduce((a, b) => a + b, 0) / avgTime[page].length) % 60)
            ).padStart(2, "0")}`
          : "0:00",
      }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 5);
  }, [analytics]);

  const deviceBreakdown = useMemo(() => {
    if (!analytics || analytics.length === 0) {
      return [
        { device: "Desktop", percentage: 0, count: 0 },
        { device: "Mobile", percentage: 0, count: 0 },
        { device: "Tablet", percentage: 0, count: 0 },
      ];
    }

    const devices = analytics.reduce((acc, curr) => {
      const device = curr.device_type || "Desktop";
      acc[device] = (acc[device] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const total = analytics.length;
    return Object.entries(devices).map(([device, count]) => ({
      device,
      count,
      percentage: Math.round((count / total) * 100),
    }));
  }, [analytics]);

  const totalVisitors = trafficData.reduce((sum, day) => sum + day.visitors, 0);
  const totalPageViews = trafficData.reduce((sum, day) => sum + day.pageViews, 0);
  const totalUnique = trafficData.reduce((sum, day) => sum + day.uniqueVisitors, 0);
  
  const avgTimeOnSite = useMemo(() => {
    if (!analytics || analytics.length === 0) return "0:00";
    const validTimes = analytics.filter((a) => a.time_on_page).map((a) => a.time_on_page);
    if (validTimes.length === 0) return "0:00";
    const avg = validTimes.reduce((a, b) => a + b, 0) / validTimes.length;
    return `${Math.floor(avg / 60)}:${String(Math.floor(avg % 60)).padStart(2, "0")}`;
  }, [analytics]);

  const bounceRate = useMemo(() => {
    if (!analytics || analytics.length === 0) return "0%";
    const bounces = analytics.filter((a) => !a.time_on_page || a.time_on_page < 10).length;
    return `${Math.round((bounces / analytics.length) * 100)}%`;
  }, [analytics]);

  // Calculate period comparison
  const midPoint = Math.floor(trafficData.length / 2);
  const currentPeriodData = trafficData.slice(midPoint);
  const previousPeriodData = trafficData.slice(0, midPoint);

  const currentVisitors = currentPeriodData.reduce((sum, day) => sum + day.visitors, 0);
  const prevVisitors = previousPeriodData.reduce((sum, day) => sum + day.visitors, 0);
  const visitorsChange = prevVisitors ? ((currentVisitors - prevVisitors) / prevVisitors) * 100 : 0;

  const currentPageViews = currentPeriodData.reduce((sum, day) => sum + day.pageViews, 0);
  const prevPageViews = previousPeriodData.reduce((sum, day) => sum + day.pageViews, 0);
  const pageViewsChange = prevPageViews ? ((currentPageViews - prevPageViews) / prevPageViews) * 100 : 0;

  const StatBadge = ({ value }: { value: number }) => {
    const isPositive = value > 0;
    return (
      <span className={`flex items-center text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? <ArrowUp className="h-3 w-3 mr-1" /> : <ArrowDown className="h-3 w-3 mr-1" />}
        {Math.abs(value).toFixed(1)}%
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Website Traffic</h2>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="60">Last 60 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Live Visitors - Last 30 minutes */}
      <LiveVisitors />

      {/* Geographic Visitor Map */}
      <VisitorMap period={period} />

      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Visitors</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalVisitors.toLocaleString()}</div>
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-muted-foreground">{totalUnique} unique visitors</p>
              {prevVisitors > 0 && <StatBadge value={visitorsChange} />}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Page Views</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalPageViews.toLocaleString()}</div>
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-muted-foreground">Last {period} days</p>
              {prevPageViews > 0 && <StatBadge value={pageViewsChange} />}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Time on Site</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgTimeOnSite}</div>
            <p className="text-xs text-muted-foreground mt-1">Average session</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Bounce Rate</CardTitle>
            <MousePointer className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{bounceRate}</div>
            <p className="text-xs text-muted-foreground mt-1">Bounce rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Traffic Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Traffic Overview</CardTitle>
          <CardDescription>Daily visitors and page views</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={trafficData}>
              <defs>
                <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorPageViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--chart-2))" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="hsl(var(--chart-2))" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="date" className="text-xs" />
              <YAxis className="text-xs" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="visitors" 
                stroke="hsl(var(--primary))" 
                fillOpacity={1} 
                fill="url(#colorVisitors)"
                strokeWidth={2}
              />
              <Area 
                type="monotone" 
                dataKey="pageViews" 
                stroke="hsl(var(--chart-2))" 
                fillOpacity={1} 
                fill="url(#colorPageViews)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Top Pages */}
        <Card>
          <CardHeader>
            <CardTitle>Top Pages</CardTitle>
            <CardDescription>Most visited pages</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topPages.length > 0 ? topPages.map((page, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{page.page}</p>
                      <p className="text-xs text-muted-foreground">Avg. {page.avgTime}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold">{page.views.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">views</p>
                  </div>
                </div>
              )) : (
                <p className="text-sm text-muted-foreground text-center py-4">No page data available</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Device Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Device Breakdown</CardTitle>
            <CardDescription>Traffic by device type</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={deviceBreakdown} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis type="number" className="text-xs" />
                <YAxis dataKey="device" type="category" className="text-xs" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--background))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px'
                  }}
                />
                <Bar dataKey="count" fill="hsl(var(--primary))" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 space-y-2">
              {deviceBreakdown.map((device, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{device.device}</span>
                  <span className="font-medium">{device.percentage}%</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}